import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit2, 
  Calendar, 
  DollarSign, 
  User, 
  Building, 
  Phone, 
  Mail, 
  Globe, 
  Target,
  Star,
  Activity,
  Clock,
  Trophy,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  FileText,
  Award,
  Briefcase,
  BarChart3,
  Users,
  ExternalLink,
  Paperclip,
  ArrowRight,
  Tag
} from 'lucide-react';
import AttachmentServiceV2 from '../../components/Attachments/AttachmentServiceV2';
import TagService from '../../components/Tags/TagService';
import { formatCurrency } from '../../utils/currency';
import { api } from '../../utils/api';
import entityRelationService from '../../services/entityRelationService';
import CustomAttributesDisplay from '../../components/CustomAttributes/CustomAttributesDisplay';
import { extractCustomAttributeFields } from '../../components/CustomAttributes/utils';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { useAuth } from '../../contexts/AuthContext';

const OpportunityDetail = ({ opportunity: propOpportunity, onEdit, onClose }) => {
  const { t } = useTranslation();
  const safeT = (key, fallback) => t(key, fallback);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantCode =
    user?.selectedTenant?.code ||
    user?.selectedTenant?.tenant_code ||
    user?.tenantCode ||
    null;
  const { config: entityConfig, loading: configLoading, error: configError } = useEntityConfig('opportunity');
  const [opportunity, setOpportunity] = useState(propOpportunity);
  const [loading, setLoading] = useState(!propOpportunity);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [related, setRelated] = useState({ customers: [], leads: [], activities: [] });
  const [entityRelationsUnified, setEntityRelationsUnified] = useState([]);

  const customAttributeFields = React.useMemo(() => {
    if (!entityConfig) return [];
    try {
      return extractCustomAttributeFields(entityConfig, {
        tenantCode,
        includeGlobal: true
      });
    } catch (err) {
      console.error('OpportunityDetail: Failed to extract custom attribute fields', err);
      return [];
    }
  }, [entityConfig, tenantCode]);

  const opportunityAttributes = React.useMemo(() => {
    const raw = opportunity?.Attributes ?? opportunity?.attributes;
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw) || {};
      } catch (error) {
        console.warn('OpportunityDetail: Failed to parse attributes JSON', error);
        return {};
      }
    }
    return {};
  }, [opportunity?.Attributes, opportunity?.attributes]);



  useEffect(() => {
    if (!propOpportunity && id) {
      fetchOpportunityDetails();
    }
  }, [id, propOpportunity]);
  // Load related Customers, Leads, and Activities for this Opportunity (both directions where applicable)
  useEffect(() => {
    const loadRelations = async () => {
      const opportunityId = (propOpportunity?.OpportunityID) || opportunity?.OpportunityID || id;
      if (!opportunityId) return;

      try {
        // Customers as target (Opportunity -> Customer)
        const custAsTargetResp = await entityRelationService.getRelatedEntitiesByType('Opportunity', Number(opportunityId), 'Customer');
        const custAsTarget = (custAsTargetResp && custAsTargetResp.relations) || [];

        // Customers as source (Customer -> Opportunity) where this opportunity is target
        const custAsSourceResp = await api.get(`/entityrelations/by-target/Opportunity/${opportunityId}/Customer`);
        const custAsSource = (custAsSourceResp && custAsSourceResp.relations) || [];

        const customerIdsFromTarget = custAsTarget.map(r => r.TargetEntityID).filter(Boolean);
        const customerIdsFromSource = custAsSource.map(r => r.SourceEntityID).filter(Boolean);
        const uniqueCustomerIds = Array.from(new Set([...customerIdsFromTarget, ...customerIdsFromSource]));

        let customers = [];
        if (uniqueCustomerIds.length > 0) {
          const results = await Promise.all(uniqueCustomerIds.map(async (cid) => {
            try {
              const detail = await api.get(`/customers/${cid}`);
              return detail?.customer || detail || { CustomerID: cid };
            } catch {
              return { CustomerID: cid, _fetchError: true };
            }
          }));
          customers = results;
        }

        // Leads as target (Opportunity -> Lead)
        const leadsAsTargetResp = await entityRelationService.getRelatedEntitiesByType('Opportunity', Number(opportunityId), 'Lead');
        const leadsAsTarget = (leadsAsTargetResp && leadsAsTargetResp.relations) || [];

        // Leads as source (Lead -> Opportunity) where this opportunity is target
        const leadsAsSourceResp = await api.get(`/entityrelations/by-target/Opportunity/${opportunityId}/Lead`);
        const leadsAsSource = (leadsAsSourceResp && leadsAsSourceResp.relations) || [];

        const leadIdsFromTarget = leadsAsTarget.map(r => r.TargetEntityID).filter(Boolean);
        const leadIdsFromSource = leadsAsSource.map(r => r.SourceEntityID).filter(Boolean);
        const uniqueLeadIds = Array.from(new Set([...leadIdsFromTarget, ...leadIdsFromSource]));

        let leads = [];
        if (uniqueLeadIds.length > 0) {
          const leadResults = await Promise.all(uniqueLeadIds.map(async (lid) => {
            try {
              const detail = await api.get(`/leads/${lid}`);
              return detail?.lead || detail || { LeadID: lid };
            } catch {
              return { LeadID: lid, _fetchError: true };
            }
          }));
          leads = leadResults;
        }

        // Activities (Opportunity -> Activity) with data
        const activitiesResp = await entityRelationService.getRelatedEntitiesByType('Opportunity', Number(opportunityId), 'Activity');
        const activities = (activitiesResp && activitiesResp.relations) || [];

        setRelated({ customers, leads, activities });
      } catch (e) {
        // Non-fatal: keep UI working without relations
        setRelated({ customers: [], leads: [], activities: [] });
      }
    };

    loadRelations();
  }, [opportunity?.OpportunityID, propOpportunity?.OpportunityID, id]);

  // Helpers for entity relations UI (icons, paths, and colors)
  const getEntityColors = (entityType, customerType = null) => {
    // For Customer entities, use different colors based on CustomerType
    if (entityType === 'Customer' && customerType) {
      if (customerType === 'Person') {
        // Customer (Person) = blue
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          hoverBg: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
          icon: 'text-blue-600 dark:text-blue-400',
          badge: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
        };
      } else {
        // Customer (Company) = green
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      }
    }
    
    switch (entityType) {
      case 'Customer':
        // Default Customer (fallback) = green (Company)
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      case 'Lead':
        // Lead = red
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          hoverBg: 'hover:bg-red-100 dark:hover:bg-red-900/30',
          icon: 'text-red-600 dark:text-red-400',
          badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
        };
      case 'Opportunity':
        // Opportunity = violet
        return {
          bg: 'bg-violet-50 dark:bg-violet-900/20',
          border: 'border-violet-200 dark:border-violet-800',
          hoverBg: 'hover:bg-violet-100 dark:hover:bg-violet-900/30',
          icon: 'text-violet-600 dark:text-violet-400',
          badge: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200'
        };
      case 'Activity':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          hoverBg: 'hover:bg-orange-100 dark:hover:bg-orange-900/30',
          icon: 'text-orange-600 dark:text-orange-400',
          badge: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
        };
      case 'SaleNote':
        // SaleNote = yellow
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          hoverBg: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
          icon: 'text-yellow-600 dark:text-yellow-400',
          badge: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-700',
          border: 'border-gray-200 dark:border-gray-700',
          hoverBg: 'hover:bg-gray-100 dark:hover:bg-gray-600',
          icon: 'text-gray-600 dark:text-gray-400',
          badge: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
        };
    }
  };

  const getEntityIcon = (entityType) => {
    const colors = getEntityColors(entityType);
    const iconClass = `h-4 w-4 ${colors.icon}`;
    
    switch (entityType) {
      case 'Customer': return <Building className={iconClass} />;
      case 'Lead': return <User className={iconClass} />;
      case 'Opportunity': return <Target className={iconClass} />;
      case 'Activity': return <Calendar className={iconClass} />;
      case 'SaleNote': return <FileText className={iconClass} />;
      default: return <FileText className={iconClass} />;
    }
  };

  const getEntityPath = (entityType, entityId) => {
    switch (entityType) {
      case 'Customer': return `/customers/${entityId}`;
      case 'Lead': return `/leads/${entityId}`;
      case 'Opportunity': return `/opportunities/${entityId}`;
      case 'Activity': return `/activities/${entityId}`;
      case 'SaleNote': return `/notes?noteId=${entityId}`;
      default: return '#';
    }
  };

  const getEntityName = (rel) => {
    // Try to get a meaningful name from the relation data
    if (rel.EntityName) return rel.EntityName;
    return `${rel.EntityType} #${rel.EntityID}`;
  };

  // Unified Entity Relations (similar to Customer/Lead pages)
  useEffect(() => {
    const loadUnifiedRelations = async () => {
      const opportunityId = (propOpportunity?.OpportunityID) || opportunity?.OpportunityID || id;
      if (!opportunityId) return;
      try {
        const relationsResp = await entityRelationService.getRelatedEntities('Opportunity', Number(opportunityId), { direction: 'both', timeoutMs: 12000 });
        const relations = Array.isArray(relationsResp) ? relationsResp : (relationsResp?.relations || []);
        
        const truncate = (text, max = 80) => {
          if (!text) return '';
          const trimmed = String(text).trim();
          return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
        };

        const mapped = relations.map(rel => {
          const srcType = rel.SourceEntityType || rel.sourceEntityType;
          const tgtType = rel.TargetEntityType || rel.targetEntityType;
          const srcId = rel.SourceEntityID || rel.sourceEntityID;
          const tgtId = rel.TargetEntityID || rel.targetEntityID;
          const isOpportunitySource = (srcType || '').toString().toLowerCase() === 'opportunity';
          const entityTypeRaw = (isOpportunitySource ? tgtType : srcType) || '';
          const entityTypeLower = (entityTypeRaw || '').toString().toLowerCase();
          const entityType = entityTypeLower === 'customer' ? 'Customer'
            : entityTypeLower === 'lead' ? 'Lead'
            : entityTypeLower === 'opportunity' ? 'Opportunity'
            : entityTypeLower === 'activity' ? 'Activity'
            : entityTypeLower === 'salenote' || entityTypeLower === 'salesnote' ? 'SaleNote'
            : (entityTypeRaw || '');
          const entityId = isOpportunitySource ? tgtId : srcId;

          // Try to derive a display name from relation fields if present
          let entityName = '';
          switch (entityType) {
            case 'Customer':
              entityName = rel.CustomerName || `${rel.FirstName || ''} ${rel.LastName || ''}`.trim() || rel.CompanyName || `Customer ${entityId}`;
              break;
            case 'Lead':
              entityName = rel.Title || rel.ContactName || rel.CompanyName || `Lead ${entityId}`;
              break;
            case 'Opportunity':
              entityName = rel.OpportunityName || `Opportunity ${entityId}`;
              break;
            case 'Activity':
              entityName = rel.Subject || `${rel.ActivityType || 'Activity'} ${entityId}`;
              break;
            case 'SaleNote':
              entityName = truncate((rel.NoteText ? String(rel.NoteText).split('\n')[0] : '')) || `Sale Note ${entityId}`;
              break;
            default:
              entityName = `${entityType} ${entityId}`;
          }

          return {
            EntityType: entityType,
            EntityID: entityId,
            EntityName: entityName,
            RelationID: rel.RelationID || rel.relationId,
            RelationType: rel.RelationType || rel.relationType,
            // Store CustomerType if available in relation data
            CustomerType: entityType === 'Customer' ? (rel.CustomerType || null) : undefined
          };
        });

        // Enrich all relations - especially Customer entities to get CustomerType
        const enriched = await Promise.all(mapped.map(async (m) => {
          try {
            // For Customer entities, always enrich to get CustomerType if not already available
            if (m.EntityType === 'Customer' && !m.CustomerType) {
              try {
                const detail = await api.get(`/customers/${m.EntityID}`);
                const cust = detail?.customer || detail || {};
                const customerType = cust.CustomerType || 'Company';
                const name = customerType === 'Person'
                  ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                  : cust.CompanyName || cust.CustomerName;
                return { ...m, EntityName: name || m.EntityName, CustomerType: customerType };
              } catch (e) {
                console.warn('⚠️ [OpportunityDetail] Failed to enrich Customer relation:', e);
                return { ...m, CustomerType: 'Company' };
              }
            }
            
            // Only fetch if we don't have a good name
            const hasDefaultName = !m.EntityName || m.EntityName === `${m.EntityType} ${m.EntityID}`;
            if (!hasDefaultName) return m;
            
            let detail;
            if (m.EntityType === 'Customer') {
              detail = await api.get(`/customers/${m.EntityID}`);
              const cust = detail?.customer || detail || {};
              const customerType = cust.CustomerType || m.CustomerType || 'Company';
              const name = customerType === 'Person'
                ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                : cust.CompanyName || cust.CustomerName;
              return { ...m, EntityName: name || m.EntityName, CustomerType: customerType };
            } else if (m.EntityType === 'Lead') {
                detail = await api.get(`/leads/${m.EntityID}`);
                const ld = detail?.lead || detail || {};
                const name = ld.Title || ld.ContactName || ld.CompanyName;
                return { ...m, EntityName: name || m.EntityName };
              } else if (m.EntityType === 'Opportunity') {
                detail = await api.get(`/opportunities/${m.EntityID}`);
                const opp = detail?.opportunity || detail || {};
                const name = opp.OpportunityName || opp.CustomerName;
                return { ...m, EntityName: name || m.EntityName };
              } else if (m.EntityType === 'Activity') {
                detail = await api.get(`/activities/${m.EntityID}`);
                const act = detail?.activity || detail || {};
                const name = act.Subject || `${act.ActivityType || 'Activity'} ${m.EntityID}`;
                return { ...m, EntityName: name || m.EntityName };
              } else if (m.EntityType === 'SaleNote') {
                try {
                  detail = await api.get(`/sales-notes/${m.EntityID}`);
                  const sn = detail?.saleNote || detail || {};
                  const name = truncate(sn.NoteText ? String(sn.NoteText).split('\n')[0] : (sn.Title || ''));
                  return { ...m, EntityName: name || m.EntityName };
                } catch {
                  return m;
                }
              }
          } catch (e) {
            console.warn('⚠️ [OpportunityDetail] Failed to enrich relation name for', m, e);
            return m;
          }
          return m;
        }));
        setEntityRelationsUnified(enriched);
      } catch (e) {
        console.warn('[OpportunityDetail] Failed to load entity relations:', e);
        setEntityRelationsUnified([]);
      }
    };
    loadUnifiedRelations();
  }, [opportunity?.OpportunityID, propOpportunity?.OpportunityID, id]);





  const fetchOpportunityDetails = async (opportunityId = null) => {
    try {
      setLoading(true);
      const targetId = opportunityId || id || opportunity?.OpportunityID;
      
      if (!targetId) {
        console.error('No opportunity ID available for fetching details');
        return;
      }
      
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/opportunities/${targetId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch opportunity details');
      }

      const data = await response.json();

      setOpportunity(data.opportunity || data);
    } catch (err) {
      console.error('Error in fetchOpportunityDetails:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else {
      navigate(`/opportunities/${id}/edit`);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/opportunities');
    }
  };


  
  if (loading || configLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || configError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error || configError}</p>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-600">{safeT('opportunities.opportunityNotFound', 'Opportunity not found')}</p>
      </div>
    );
  }

  // Helper function to format currency (now using utility)
  const formatCurrencyLocal = (amount, currency = 'THB') => {
    return formatCurrency(amount, currency);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Lead': return 'bg-gray-100 text-gray-800';
      case 'Qualified': return 'bg-blue-100 text-blue-800';
      case 'Proposal': return 'bg-yellow-100 text-yellow-800';
      case 'Negotiation': return 'bg-orange-100 text-orange-800';
      case 'Closed Won': return 'bg-green-100 text-green-800';
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 80) return 'text-green-600';
    if (probability >= 60) return 'text-yellow-600';
    if (probability >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'Lead': return Target;
      case 'Qualified': return CheckCircle;
      case 'Proposal': return FileText;
      case 'Negotiation': return TrendingUp;
      case 'Closed Won': return Trophy;
      case 'Closed Lost': return AlertCircle;
      default: return Activity;
    }
  };

  const formatOpportunityName = (opportunity) => {
    return opportunity.OpportunityName || `Opportunity #${opportunity.OpportunityID}`;
  };
  const getOpportunityTitle = (op) => (op?.OpportunityName || '-');

  const StageIcon = getStageIcon(opportunity?.Stage);

  const tabs = [
    { id: 'overview', label: safeT('opportunities.overview', 'Overview'), icon: BarChart3 },
    { id: 'details', label: safeT('opportunities.details', 'Details'), icon: FileText },
    { id: 'timeline', label: safeT('opportunities.timeline', 'Timeline'), icon: Calendar },
    { id: 'notes', label: safeT('opportunities.notes', 'Notes'), icon: FileText },
    { id: 'attachments', label: safeT('opportunities.attachments', 'Attachments'), icon: FileText },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header - Mobile Optimized */}
      <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
        <div className="flex items-start space-x-2 sm:space-x-4">
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 touch-target"
          >
            <ArrowLeft className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
          <div className="flex items-start space-x-2 sm:space-x-3 min-w-0 flex-1">
            <div className={`flex-shrink-0 p-2 rounded-lg ${getStageColor(opportunity.Stage)}`}>
              <StageIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">{formatOpportunityName(opportunity)}</h1>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1 break-words">
                {opportunity.CustomerName}{opportunity.ContactName ? ` • ${opportunity.ContactName}` : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleEdit}
            className="flex-1 sm:flex-none px-3 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight"
          >
            <Edit2 className="h-4 w-4 flex-shrink-0" />
            <span>{safeT('common.edit', 'Edit')}</span>
          </button>
        </div>
      </div>

      {/* Key Metrics - Mobile Optimized */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4 md:mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{safeT('opportunities.value', 'Value')}</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrencyLocal(opportunity.Value, opportunity.Currency)}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-500 dark:text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{safeT('opportunities.probability', 'Probability')}</p>
              <p className={`text-2xl font-bold ${getProbabilityColor(opportunity.Probability)}`}>
                {opportunity.Probability}%
              </p>
            </div>
            <Star className="h-8 w-8 text-yellow-500 dark:text-yellow-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{safeT('opportunities.stage', 'Stage')}</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{opportunity.Stage}</p>
            </div>
            <StageIcon className="h-8 w-8 text-blue-500 dark:text-blue-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{safeT('opportunities.expectedCloseDate', 'Expected Close Date')}</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatDate(opportunity.ExpectedCloseDate)}
              </p>
            </div>
            <Calendar className="h-8 w-8 text-purple-500 dark:text-purple-400" />
          </div>
        </div>
      </div>

      {/* Status and Priority Badges */}
      <div className="flex items-center space-x-2 mb-6">
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStageColor(opportunity.Stage)}`}>
          {opportunity.Stage}
        </span>
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getPriorityColor(opportunity.Priority)}`}>
          {opportunity.Priority}
        </span>
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
          opportunity.IsActive ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
        }`}>
          {opportunity.IsActive ? 'Active' : 'Inactive'}
        </span>
        {opportunity.IsHighValue && (
          <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
            High Value
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Target className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.basicInfo', 'Basic Information')}</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.opportunityName', 'Opportunity Name')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{getOpportunityTitle(opportunity)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.category', 'Category')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{opportunity.Category || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.industry', 'Industry')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{opportunity.Industry || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.leadSource', 'Lead Source')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{opportunity.LeadSource || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.salesTeam', 'Sales Team')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{opportunity.SalesTeam || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.estimatedEffort', 'Estimated Effort')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{opportunity.EstimatedEffort ? `${opportunity.EstimatedEffort} hours` : '-'}</p>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {safeT('opportunities.description', 'Description')}
              </label>
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunity.Description || '-'}</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.contactInfo', 'Contact Information')}</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.customerName', 'Customer Name')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{opportunity.CustomerName || '-'}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.contactName', 'Contact Name')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{opportunity.ContactName || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.contactEmail', 'Contact Email')}
                </label>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  {opportunity.ContactEmail && opportunity.ContactEmail !== '-' && opportunity.ContactEmail.includes('@') ? (
                    <a 
                      href={`mailto:${opportunity.ContactEmail}`}
                      className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 hover:underline text-gray-900 dark:text-white"
                      title={`Send email to ${opportunity.ContactEmail}`}
                    >
                      {opportunity.ContactEmail}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-900 dark:text-white">{opportunity.ContactEmail || '-'}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.contactPhone', 'Contact Phone')}
                </label>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <p className="text-sm text-gray-900 dark:text-white">{opportunity.ContactPhone || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.timeline', 'Timeline')}</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.expectedCloseDate', 'Expected Close Date')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(opportunity.ExpectedCloseDate)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.lastActivityDate', 'Last Activity Date')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(opportunity.LastActivityDate)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.nextFollowUpDate', 'Next Follow-up Date')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(opportunity.NextFollowUpDate)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.createdAt', 'Created At')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDateTime(opportunity.CreatedAt)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('opportunities.updatedAt', 'Updated At')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDateTime(opportunity.UpdatedAt)}</p>
              </div>
            </div>
          </div>

          {/* Notes and Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.notesAndDetails', 'Notes & Details')}</h3>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {safeT('opportunities.notes', 'Notes')}
                </label>
                <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunity.Notes || '-'}</p>
              </div>

              {opportunity.Requirements && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {safeT('opportunities.requirements', 'Requirements')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunity.Requirements}</p>
                </div>
              )}

              {opportunity.NextStepAction && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {safeT('opportunities.nextStepAction', 'Next Step Action')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunity.NextStepAction}</p>
                </div>
              )}

              {opportunity.CompetitorInfo && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {safeT('opportunities.competitorInfo', 'Competitor Info')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunity.CompetitorInfo}</p>
                </div>
              )}
            </div>
          </div>

        {customAttributeFields.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <CustomAttributesDisplay
              fields={customAttributeFields}
              attributes={opportunityAttributes}
                title={safeT('opportunities.additionalDataSection', 'Opportunity Additional Data')}
              entityName="opportunity"
              onFieldsUpdated={async () => {
                if (propOpportunity?.OpportunityID) {
                  window.location.reload();
                }
              }}
            />
          </div>
        )}

          {/* Related Activities */}
          {related.activities.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Activity className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.relatedActivities', 'Related Activities')}</h3>
                <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs px-2 py-1 rounded-full">{related.activities.length}</span>
              </div>
              <div className="space-y-4">
                {related.activities.slice(0, 5).map((act) => (
                  <div
                    key={act.ActivityID}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    onClick={() => navigate(`/activities/${act.ActivityID}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                            {act.Subject}
                          </h4>
                          <ExternalLink className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          {act.ActivityType && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">{act.ActivityType}</span>
                          )}
                          {act.Status && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">{act.Status}</span>
                          )}
                          {act.Priority && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">{act.Priority}</span>
                          )}
                        </div>
                        {act.ActivityDescription && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">{act.ActivityDescription}</p>
                        )}
                        <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            Due: {act.DueDate ? new Date(act.DueDate).toLocaleDateString() : 'Not set'}
                          </span>
                          {act.SalespersonName && (
                            <span>By: {act.SalespersonName}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {related.activities.length > 5 && (
                  <div className="text-center pt-4">
                    <button
                      onClick={() => navigate(`/activities?entityType=Opportunity&entityId=${opportunity.OpportunityID}`)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
                    >
                      {safeT('opportunities.viewAllActivities', `View all ${related.activities.length} activities`)}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Attachments */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Paperclip className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.attachments', 'Attachments')}</h3>
            </div>
            <AttachmentServiceV2 
              entityType="Opportunity" 
              entityId={opportunity.OpportunityID} 
              onAttachmentChange={() => {
                // Optional: Refresh opportunity data or show notification
              }}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Entity Relations */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('common.relatedItems', 'Relationships')}</h3>
              {entityRelationsUnified.length > 0 && (
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded-full">{entityRelationsUnified.length}</span>
              )}
            </div>
            <div>
              {entityRelationsUnified.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">{safeT('opportunities.noEntityRelations', 'No entity relations')}</p>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {(() => {
                    // Group entities by type
                    const grouped = entityRelationsUnified.reduce((acc, rel) => {
                      const type = rel.EntityType;
                      if (!acc[type]) {
                        acc[type] = [];
                      }
                      acc[type].push(rel);
                      return acc;
                    }, {});

                    // Define display order: Customer first, then Activity, then others
                    const typeOrder = ['Customer', 'Activity', 'Lead', 'Opportunity', 'SaleNote'];
                    const sortedTypes = Object.keys(grouped).sort((a, b) => {
                      const aIndex = typeOrder.indexOf(a);
                      const bIndex = typeOrder.indexOf(b);
                      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                      if (aIndex === -1) return 1;
                      if (bIndex === -1) return -1;
                      return aIndex - bIndex;
                    });

                    return sortedTypes.map((entityType) => {
                      const entities = grouped[entityType];
                      // For Customer type, sort by CustomerType (Company first, then Person)
                      const sortedEntities = entityType === 'Customer' 
                        ? [...entities].sort((a, b) => {
                            const aType = a.CustomerType || 'Company';
                            const bType = b.CustomerType || 'Company';
                            if (aType === bType) return 0;
                            return aType === 'Company' ? -1 : 1;
                          })
                        : entities;

                      return (
                        <div key={entityType} className="space-y-2">
                          <div className="flex items-center space-x-2 mb-2">
                            {getEntityIcon(entityType)}
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                              {entityType} ({entities.length})
                            </h4>
                          </div>
                          <div className="space-y-2">
                            {sortedEntities.map((rel, idx) => {
                              const colors = getEntityColors(rel.EntityType, rel.CustomerType);
                              const path = getEntityPath(rel.EntityType, rel.EntityID);
                              const isPersonCustomer = rel.EntityType === 'Customer' && rel.CustomerType === 'Person';

                              return (
                                <div
                                  key={rel.RelationID || `${rel.EntityType}-${rel.EntityID}-${idx}`}
                                  className={`flex items-center space-x-3 p-3 rounded-lg border ${colors.border} ${colors.bg} ${colors.hoverBg} cursor-pointer transition-all ${
                                    isPersonCustomer ? 'ml-6' : ''
                                  }`}
                                  onClick={() => {
                                    if (path !== '#') navigate(path);
                                  }}
                                >
                                  {getEntityIcon(rel.EntityType)}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{getEntityName(rel)}</p>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${colors.badge}`}>
                                        {rel.EntityType}
                                        {rel.CustomerType && ` (${rel.CustomerType === 'Person' ? 'Individual' : 'Company'})`}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">ID: {rel.EntityID}</span>
                                    </div>
                                  </div>
                                  <ArrowRight className={`h-4 w-4 ${colors.icon} flex-shrink-0`} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
          {/* Stage Progress */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <TrendingUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.stageProgress', 'Stage Progress')}</h3>
            </div>
            <div className="text-center">
              <div className={`p-3 rounded-lg ${getStageColor(opportunity.Stage)}`}>
                <StageIcon className="h-8 w-8 mx-auto mb-2" />
                <p className="font-semibold">{opportunity.Stage}</p>
              </div>
              <div className="mt-4">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${opportunity.Probability || 0}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{opportunity.Probability || 0}% probability</p>
              </div>
            </div>
          </div>

          {/* Value Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.valueInfo', 'Value Information')}</h3>
            </div>
            <div className="space-y-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatCurrencyLocal(opportunity.Value, opportunity.Currency)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Value</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">{opportunity.Probability || 0}%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Probability</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                    {opportunity.Value && opportunity.Probability 
                      ? formatCurrencyLocal((opportunity.Value * opportunity.Probability) / 100, opportunity.Currency)
                      : '-'
                    }
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Expected Value</p>
                </div>
              </div>
            </div>
          </div>

          {/* Priority & Status */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Star className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('opportunities.priorityStatus', 'Priority & Status')}</h3>
            </div>
            <div className="space-y-3">
              <div className="text-center">
                <span className={`inline-flex px-4 py-2 text-lg font-semibold rounded-full border ${getPriorityColor(opportunity.Priority)}`}>
                  {opportunity.Priority || 'Not Set'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    opportunity.IsActive ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {opportunity.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    opportunity.IsHighValue ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {opportunity.IsHighValue ? 'High Value' : 'Standard'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tags Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Tag className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('common.tags', 'Tags')}</h3>
            </div>
            <TagService
              entityType="Opportunity"
              entityId={opportunity.OpportunityID}
              initialTags={opportunity.Tags ? (typeof opportunity.Tags === 'string' ? opportunity.Tags.split(',').map(t => t.trim()).filter(Boolean) : opportunity.Tags) : []}
              maxTags={15}
              onChange={async (newTags) => {
                // Refresh opportunity data after tag change
                await fetchOpportunityDetails(opportunity.OpportunityID);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetail; 
